/**
 * Utility function to format prices with K and M notation
 *
 * Rules:
 * - If price >= 1,000,000: format as millions (e.g., 1,200,000 → 1.2M)
 * - If price >= 1,000: format as thousands (e.g., 1,500 → 1.5K)
 * - Otherwise: return original price as string
 */
export function formatPriceWithNotation(price: number | string): string {
  // Convert to number if it's a string
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;

  // Handle invalid numbers
  if (isNaN(numericPrice) || numericPrice === null || numericPrice === undefined) {
    return String(price);
  }

  // Handle negative numbers
  const isNegative = numericPrice < 0;
  const absolutePrice = Math.abs(numericPrice);

  // Format millions (>= 1,000,000)
  if (absolutePrice >= 1000000) {
    const millions = absolutePrice / 1000000;
    const formatted = millions % 1 === 0 ? millions.toString() : Math.floor(millions * 10) / 10;
    return isNegative ? `-${formatted}M` : `${formatted}M`;
  }

  // Format thousands (>= 1,000)
  if (absolutePrice >= 1000) {
    const thousands = absolutePrice / 1000;
    const formatted = thousands % 1 === 0 ? thousands.toString() : Math.floor(thousands * 10) / 10;
    return isNegative ? `-${formatted}K` : `${formatted}K`;
  }

  // Return original price for values < 1,000
  return String(price);
}

/**
 * Enhanced version that preserves currency symbols and formatting
 * Extracts the numeric value, formats it, and reattaches currency symbols
 */
export function formatPriceWithCurrencyNotation(priceString: string): string {
  // Extract currency symbol and numeric value
  const currencyRegex = /^([^\d]*)([\d.,]+)(.*)$/;
  const currencyMatch = currencyRegex.exec(priceString);

  if (!currencyMatch) {
    return priceString; // Return original if no match
  }

  const [, prefix, numericPart, suffix] = currencyMatch;

  // Clean the numeric part (remove commas, handle different decimal separators)
  const cleanNumeric = numericPart.replace(/,/g, '').replace(',', '.');
  const numericValue = parseFloat(cleanNumeric);

  if (isNaN(numericValue)) {
    return priceString; // Return original if not a valid number
  }

  // Check if it's INR currency - if so, don't apply K/M notation, just format as whole number
  const isINR = prefix.includes('₹');

  let formattedNumeric: string;
  if (isINR) {
    // For INR, round to whole number and format with Indian number system, no K/M notation
    const roundedValue = Math.round(numericValue);
    formattedNumeric = roundedValue.toLocaleString('en-IN');
  } else {
    // For other currencies, apply K/M notation as before
    formattedNumeric = formatPriceWithNotation(numericValue);
  }

  // Reconstruct the price string
  return `${prefix}${formattedNumeric}${suffix}`;
}

/**
 * Formats a price for tooltip display with proper currency symbol and comma formatting
 * @param price - The price value (string or number)
 * @param currencySymbol - Optional currency symbol, will be extracted from price if not provided
 * @returns string - Formatted price with currency symbol and commas
 */
export function formatPriceForTooltip(price: string | number, currencySymbol?: string): string {
  // Handle different price formats
  let numericPrice: number;
  if (typeof price === 'string') {
    // Remove currency symbols (including ₹, $, €, £, etc.) and clean the string
    const cleanPrice = price.replace(/[₹$€£¥,]/g, '').trim();
    numericPrice = parseFloat(cleanPrice);
  } else {
    numericPrice = price;
  }

  // Handle NaN case
  if (isNaN(numericPrice)) {
    return '$0.00';
  }

  // Extract currency symbol from original price if not provided
  let finalCurrencySymbol = currencySymbol;
  if (!finalCurrencySymbol && typeof price === 'string') {
    const currencyRegex = /[₹$€£¥]/;
    const currencyMatch = currencyRegex.exec(price);
    finalCurrencySymbol = currencyMatch?.[0] || '$';
  } else if (!finalCurrencySymbol) {
    finalCurrencySymbol = '$';
  }

  // Determine decimal places based on currency symbol
  const decimalPlaces = getDecimalPlacesForCurrency(finalCurrencySymbol);

  // Format number with commas for better readability
  const formattedPrice = numericPrice.toLocaleString('en-IN', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });

  return `${finalCurrencySymbol}${formattedPrice}`;
}

/**
 * Determines the number of decimal places to display for a given currency symbol
 * @param currencySymbol - The currency symbol (₹, $, €, etc.)
 * @returns number - Number of decimal places (0 for INR, 2 for most others)
 */
function getDecimalPlacesForCurrency(currencySymbol: string): number {
  // INR should display without decimal places
  if (currencySymbol === '₹') {
    return 0;
  }

  // All other currencies use 2 decimal places
  return 2;
}

/**
 * Centralized price conversion utility
 * Converts prices from cents to dollars consistently across the application
 * @param priceInCents - Price in cents
 * @returns number - Price in dollars
 */
export function convertCentsToDollars(priceInCents: number): number {
  if (typeof priceInCents !== 'number' || isNaN(priceInCents)) {
    return 0;
  }
  return priceInCents / 100;
}

/**
 * Centralized price conversion utility
 * Converts prices from dollars to cents consistently across the application
 * @param priceInDollars - Price in dollars
 * @returns number - Price in cents
 */
export function convertDollarsToCents(priceInDollars: number): number {
  if (typeof priceInDollars !== 'number' || isNaN(priceInDollars)) {
    return 0;
  }
  return Math.round(priceInDollars * 100);
}

/**
 * Normalizes a price value to ensure it's a valid number
 * @param price - The price to normalize
 * @returns number - Normalized price value
 */
export function normalizePrice(price: number | string | null | undefined): number {
  if (typeof price === 'string') {
    const parsed = parseFloat(price);
    return isNaN(parsed) ? 0 : parsed;
  }
  if (typeof price === 'number' && !isNaN(price)) {
    return price;
  }
  return 0;
}
